/**
 * Axios-инстанс для cabinet → backend.
 * - baseURL из VITE_API_URL; `withCredentials` — чтобы браузер прикладывал общую
 *   cookie Драфтера `.drafter.ru` (нужна для повторного обмена при истечении access).
 * - Request-interceptor подкладывает Authorization: Bearer (локальный access).
 * - Response-interceptor: на 401 повторяем обмен по общей cookie (exchange-drafter)
 *   и ретраим запрос; не вышло — уходим на вход `id.drafter.ru`.
 *
 * Refresh-токена нет: локальный access живёт 15 мин, при истечении молча
 * повторяем обмен (cookie Драфтера долгоживущая, живёт в браузере).
 */

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import { exchangeViaCookie, redirectToDrafterId } from './drafter-auth';
import { tokenStorage } from './token-storage';

const API_BASE: string = (import.meta.env.VITE_API_URL as string) || '/api';

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  withCredentials: true,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// Дедупликация одновременных обменов: пока один в полёте, остальные
export const BLOCKED_PATH = '/blocked';

/** 403 `participant_blocked` — участник заблокирован администратором (B-2376). */
export function isParticipantBlockedError(error: unknown): boolean {
  const response = (
    error as { response?: { status?: number; data?: { error?: { code?: string } } } }
  )?.response;
  return response?.status === 403 && response.data?.error?.code === 'participant_blocked';
}

// 401-ответы ждут его, потом ретраятся с новым access-токеном.
let exchangePromise: Promise<string | null> | null = null;

async function reExchangeAccessToken(): Promise<string | null> {
  try {
    const { accessToken, leadId } = await exchangeViaCookie();
    tokenStorage.save({ accessToken, leadId });
    return accessToken;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retried) {
      original._retried = true;
      if (!exchangePromise) {
        exchangePromise = reExchangeAccessToken().finally(() => {
          exchangePromise = null;
        });
      }
      const newAccess = await exchangePromise;
      if (newAccess) {
        original.headers.set('Authorization', `Bearer ${newAccess}`);
        return api.request(original as AxiosRequestConfig);
      }
    }

    if (status === 401) {
      // Валидной сессии Драфтера нет → на форму входа IdP.
      tokenStorage.clear();
      redirectToDrafterId();
    }

    if (isParticipantBlockedError(error) && window.location.pathname !== BLOCKED_PATH) {
      // B-2376: админ заблокировал участника — бэк закрыл все ручки кабинета.
      // Один экран вместо россыпи ошибок загрузки на каждой странице.
      window.location.replace(BLOCKED_PATH);
    }

    return Promise.reject(error);
  },
);
